import { faArrowDownToLine } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useState } from 'react';
import { FileApi } from '../../openapi/dropbox';
import ErrorService from '../../services/ErrorService';
import { showErrorToast } from '../../slices/ToastNotificationSlice';
import { useAppDispatch } from '../../slices/store';
import {
  isMobileWebView,
  requestNativeFileDownload,
} from '../../utils/DeviceDetectionUtils';
import { downloadFile } from '../../utils/FileUtils';
import { getDropboxConfiguration } from '../../utils/OpenapiConfigurationUtils';

interface FileDownloadLinkProps {
  fileId: string;
  filename: string;
}

export const FileDownloadLink: React.FC<FileDownloadLinkProps> = ({
  fileId,
  filename,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();

  const handleDownload = useCallback(async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      // Get the file URL from Dropbox API
      const fileApi = new FileApi(await getDropboxConfiguration());
      const { data: fileUrl } = await fileApi.getFileUrl(fileId);

      if (!fileUrl) {
        throw new Error('File URL not found');
      }

      // Check if running in mobile WebView
      const isInWebView = isMobileWebView();

      if (isInWebView) {
        // Try to use native download bridge first
        const nativeDownloadSuccess = requestNativeFileDownload(
          fileUrl,
          filename,
        );

        if (nativeDownloadSuccess) {
          // Native app will handle the download
          return;
        }

        // Fallback: Use blob-based download for WebView
        // This works better in mobile WebView than window.open()
        await downloadFile(fileUrl, filename);
      } else {
        // Desktop browser: Use traditional window.open
        // This works fine in desktop browsers
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      dispatch(
        showErrorToast('Error downloading file. Please try again later.'),
      );
      ErrorService.notifyIgnoreAuthErrors(
        'Error downloading file',
        error as Error,
        {
          file: {
            fileId,
          },
        },
      );
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, fileId, filename, isLoading]);

  return (
    <button
      onClick={handleDownload}
      disabled={isLoading}
      className='inline-flex items-center gap-2 text-reskin-primary-blue hover:underline disabled:opacity-50 disabled:cursor-not-allowed break-all text-left'
      type='button'
    >
      <span>{filename}</span>
      <FontAwesomeIcon icon={faArrowDownToLine} className='w-4 h-4' />
    </button>
  );
};

FileDownloadLink.displayName = 'FileDownloadLink';
